import React, { Component } from 'react'
import JobForm from '../../../components/customer_portal/job/JobForm';
import JobDetail from '../../../components/customer_portal/job/JobDetail';
import Box from '@mui/material/Box';
import { Stack, Typography } from '@mui/material';
import PricingTable from '../../../components/customer_portal/job/PricingTable';
import SendMessage from '../../../components/customer_portal/job/SendMessage';
import { isAuth } from '../../../helpers/cookies';
import { get_user_company } from '../../../store/user/userSlice';
import { update_job, get_job_detail, proof_log_table, create_send_message, send_proof, get_job_messages, get_sidebar_menu } from '../../../store/job/jobSlice';
import { get_company_process_stage } from '../../../store/user/userSlice';

// Helper functions defined outside component
const ensureString = (val) => (val === null || val === undefined) ? "" : String(val);

const cleanFileList = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map(item => {
    if (!item) return null;
    if (item instanceof File) return item;
    if (typeof item === 'string') return item;
    if (item.file instanceof File) return item.file;
    if (typeof item.file === 'string') return item.file;
    if (item.file_path) return item.file_path;
    return null;
  }).filter(Boolean);
};

const cleanJobItems = (items) => {
  if (!Array.isArray(items)) return [];
  return items.map(item => ({
    ...item,
    disabled: item?.disabled === true,
    id: ensureString(item.id),
    item: ensureString(item.item),
    line_id: ensureString(item.line_id),
    product_color: ensureString(item.product_color),
    imprint_color: ensureString(item.imprint_color),
    pos_loc_placment: ensureString(item.pos_loc_placment),
    imprint_method: ensureString(item.imprint_method),
    imprint_instruction: ensureString(item.imprint_instruction),
    vector_count: ensureString(item.vector_count),
    complex_vector_count: ensureString(item.complex_vector_count),
    job_type: ensureString(item.job_type),
    complexity_level: ensureString(item.complexity_level),
    standard_job_cost: ensureString(item.standard_job_cost),
    complex_vector_cost: ensureString(item.complex_vector_cost),
    total_cost: ensureString(item.total_cost),
    cost: ensureString(item.cost),
    no_of_revisions: ensureString(item.no_of_revisions),
    revision_cost: ensureString(item.revision_cost),
    ETA: ensureString(item.ETA),
    comments: ensureString(item.comments),
    completed_time: ensureString(item.completed_time),
    submitted_file: cleanFileList(item.submitted_file),
    completed_file: cleanFileList(item.completed_file),
    eps_file: cleanFileList(item.eps_file || []),
    logo_file: cleanFileList(item.logo_file || [])
  }));
};

class JobPageContainer extends Component {
  constructor(props) {
    super(props);
    this.action_type = 1;
    this.selected_process_tab = props.selected_process_tab;
  }

  componentDidMount() {
    const params = new URLSearchParams(window.location.search);
    const processId = params.get('process_id');

    if (processId) {
      this.props.setProcessTab(Number(processId));
      this.props.setProcessHasJobForm(Number(processId));
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const { job_id, job_detail, get_user_company } = this.props;
    const auth = isAuth();
    if (auth && !this.props.user_company) {
      this.props.get_user_company();
    }
    if (job_id) {
      // Set job_id in Redux so child components can access it
      this.props.setJobId(parseInt(job_id));
      this.props.get_job_messages({ job_id: job_id });
      this.props.get_job_detail({ job_id: job_id });
      // this.props.proof_log_table({ job_id })
      const company_id = job_detail ? job_detail.company_id : (isAuth() ? isAuth().company_id : null);
      if (company_id) {
        this.props.get_company_process(company_id);
      }
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.job_id && this.props.job_id !== prevProps.job_id) {
      // Update Redux job_id when URL param changes
      this.props.setJobId(parseInt(this.props.job_id));
      this.props.get_job_messages({ job_id: this.props.job_id });
      this.props.get_job_detail({ job_id: this.props.job_id });
      // this.props.proof_log_table({ job_id: this.props.job_id });
    }

    const currentCompanyId = this.props.job_detail?.company_id;
    const prevCompanyId = prevProps.job_detail?.company_id;
    if (currentCompanyId && currentCompanyId !== prevCompanyId) {
      this.props.get_company_process(currentCompanyId);
    }
  }

  handleJobForm = (data) => {
    this.props.setJobData(data);
  }

  handleJobSubmit = async (job_data, selected_company) => {

    // 1️⃣ Collect brand selections from UI (both completed and submitted)
    const brandSelections = [
      ...(job_data.completed_file || []).filter(f => f.add_to_brand === true),
      ...(job_data.submitted_file || []).filter(f => f.add_to_brand === true)
    ];

    const backendData = {
      ...job_data,
      virtual_request: job_data?.virtual_request?.trim() || 'Req',
      submitted_file: cleanFileList(job_data.submitted_file),
      completed_file: cleanFileList(job_data.completed_file),
      logo_references: cleanFileList(job_data.logo_references),
      job_items: cleanJobItems(job_data.job_items)
    };

    // 3️⃣ Submit job
    const res = await this.props.submit_job({
      company_id: selected_company,
      process_id: this.props.selected_process_tab,
      job_data: backendData
    });


    // Extract job_id returned from backend
    const newJobId = res?.payload?.data?.id || res?.payload?.data?.job_id;
    const submittedVirtualRequest = String(job_data?.virtual_request || '').trim();
    const shouldAutoAttachJobId = !submittedVirtualRequest || submittedVirtualRequest.toLowerCase() === 'req';

    if (newJobId && shouldAutoAttachJobId) {
      const detailAction = await this.props.get_job_detail({ job_id: newJobId });
      const createdJobData = detailAction?.payload?.data?.job_data || {};
      const nextVirtualRequest = `Req${newJobId}`;

      await this.props.update_job({
        job_id: newJobId,
        job_data: {
          ...createdJobData,
          virtual_request: nextVirtualRequest
        }
      });
    }


    if (newJobId && brandSelections.length > 0) {
      // Refresh proof logs to get the new IDs
      const proofLogAction = await this.props.proof_log_table({ job_id: newJobId });
      const freshProofLogData = Array.isArray(proofLogAction.payload?.data) ? proofLogAction.payload.data : [];

      // 4️⃣ After job is saved → call brand library API
      for (const bf of brandSelections) {
        let originalName = "";
        if (bf.file instanceof File) originalName = bf.file.name;
        else if (typeof bf.file === "string") originalName = decodeURIComponent(bf.file.split("?")[0].split("/").pop());

        if (!originalName) continue;

        const uBase = originalName.split('/').pop();

        const match = freshProofLogData.find(pl => {
          if (!pl.file_path) return false;
          const cleanP = decodeURIComponent(pl.file_path).split('?')[0];
          const pBase = cleanP.split('/').pop();
          return pBase === uBase;
        });

        await this.props.add_brand({
          job_id: newJobId,
          prooflog_id: match?.id || bf.prooflog_id,
          name: bf.name,
          search_param: bf.search_param,
          is_personal: bf.is_personal,
          file_path: match?.file_path || (typeof bf.file === 'string' ? bf.file : null)
        });
      }
    }

    return res;
  };

  handleJobUpdate = async (job_data, job_id) => {
    // 1️⃣ Separate existing files (have ID) vs New files (need ID)
    const allBrandFiles = [
      ...(job_data.completed_file || []).filter(f => f.add_to_brand === true),
      ...(job_data.submitted_file || []).filter(f => f.add_to_brand === true)
    ];

    const existingBrandFiles = allBrandFiles.filter((f) => f.prooflog_id);
    const newBrandFiles = allBrandFiles.filter((f) => !f.prooflog_id);

    const cleanedData = {
      ...job_data,
      submitted_file: cleanFileList(job_data.submitted_file),
      completed_file: cleanFileList(job_data.completed_file),
      logo_references: cleanFileList(job_data.logo_references),
      job_items: cleanJobItems(job_data.job_items)
    };


    // 3️⃣ Update Job
    const result = await this.props.update_job({
      job_id,
      job_data: cleanedData,
    });

    // 4️⃣ Process pending corrections and brand files
    if (update_job.fulfilled.match(result)) {
      // Handle corrections first
      const itemsWithCorrections = (job_data.job_items || []).filter(item => item.pending_correction);
      for (const item of itemsWithCorrections) {
        const pc = item.pending_correction;
        const payload = {
          job_id,
          item_id: item.id || '',
          correction_data: {
            description: pc.description,
            correction_type: pc.correction_type,
            completed_time: pc.completed_time,
            logo_reference: Array.isArray(pc.logo_reference) ? pc.logo_reference : [],
            job_type: pc.job_type || '',
            vector_count: pc.vector_count || '',
            complex_vector_count: pc.complex_vector_count || '',
            standard_job_cost: pc.standard_job_cost || '',
            complex_vector_cost: pc.complex_vector_cost || '',
            total_cost: pc.total_cost || '',
            revision_comments: pc.revision_comments || '',
          },
          files: pc.files
        };
        await this.props.add_correction(payload);
      }

      this.handlePostUpdateProcessing(job_id, existingBrandFiles, newBrandFiles);
    }

    return result;
  };

  handlePostUpdateProcessing = async (job_id, existingBrandFiles, newBrandFiles) => {
    try {
      // 4️⃣ Refresh Job Details & Proof Logs to get new IDs
      await this.props.get_job_detail({ job_id });
      const proofLogAction = await this.props.proof_log_table({ job_id });
      const freshProofLogData = Array.isArray(proofLogAction.payload?.data) ? proofLogAction.payload.data : [];

      // 5️⃣ Match New Files to Generated IDs
      for (const newFileWrapper of newBrandFiles) {
        let originalName = "";
        if (newFileWrapper.file instanceof File) {
          originalName = newFileWrapper.file.name;
        } else if (typeof newFileWrapper.file === "string") {
          originalName = decodeURIComponent(newFileWrapper.file.split("?")[0].split("/").pop());
        }

        if (!originalName) continue;

        const uBase = originalName.split('/').pop();

        // Find matching prooflog by filename
        const match = (freshProofLogData || []).find(pl => {
          if (!pl.file_path) return false;
          const cleanP = decodeURIComponent(pl.file_path).split('?')[0];
          const pBase = cleanP.split('/').pop();
          return pBase === uBase;
        });

        if (match) {
          await this.props.add_brand({
            job_id,
            prooflog_id: match.id,
            name: newFileWrapper.name,
            search_param: newFileWrapper.search_param,
            is_personal: newFileWrapper.is_personal,
            file_path: match.file_path
          });
        }
      }

      // 6️⃣ Add Existing Files (Ids already known)
      for (const bf of existingBrandFiles) {
        await this.props.add_brand({
          job_id,
          prooflog_id: bf.prooflog_id,
          name: bf.name,
          search_param: bf.search_param,
          is_personal: bf.is_personal,
          file_path: typeof bf.file === 'string' ? bf.file : null
        });
      }

      // 7️⃣ FINAL REFRESH: Ensure state reflects all brand additions
      if (existingBrandFiles.length > 0 || newBrandFiles.length > 0) {
        // await this.props.proof_log_table({ job_id });
        // Optional: refresh job detail if needed, but proof_log is the main one for checkboxes
        await this.props.get_job_detail({ job_id });
      }
    } catch (error) {
      console.error("Post-update processing failed:", error);
    }
  };


  handleZipDownload = async (job_id, comp_file_list, ref_no) => {
    try {
      const response = await this.props.download_zip_file({
        job_id,
        comp_file_list
      }).unwrap();

      const blob = response.data || response;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const fileName = ref_no
        ? `${ref_no} - Completed files.zip`
        : "Completed files.zip";

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Zip download failed:", error);
    }
  };

  handleStatusChange = async (payload) => {
    let res = await this.props.create_send_message(payload);
    return res;
  };

  handleJobLogSubmit = async (payload) => {
    const { create_send_message: sendMessageDispatch } = this.props;
    const res = await sendMessageDispatch(payload);
    if (create_send_message.fulfilled.match(res)) {
      // await this.props.proof_log_table({ job_id: this.props.job_id });
      await this.props.get_job_messages({ job_id: this.props.job_id });
    }
    return res;
  };

  handleSendProof = async (jobId) => {
    try {
      const { job_detail, send_proof: sendProofDispatch, get_company_process_stage, get_sidebar_menu, get_job_messages, proof_log_table } = this.props;

      if (!job_detail) {
        alert("Job detail not loaded.");
        return;
      }

      const jobData = job_detail.job_data || {};
      const compFileList = job_detail.comp_file_list || [];

      if (compFileList.length === 0) {
        alert("No completed files found to send.");
        return;
      }

      const sendToCustomer =
        jobData.send_art_to_customer === true ||
        jobData.send_art_to_customer === 'true' ||
        jobData.send_art_to_customer === 1;

      let recipientEmail = "";
      if (sendToCustomer) {
        recipientEmail = jobData.customer_email || jobData.customeremail;
        if (!recipientEmail) {
          alert("No customer email found in job data.");
          return;
        }
      } else {
        recipientEmail = job_detail.creator_email;
        if (!recipientEmail) {
          alert("No job creator (Rep) email found.");
          return;
        }
      }

      const payload = {
        ref_No: jobData.ref_No || "",
        customeremail: recipientEmail,
        job_id: parseInt(jobId),
        note: jobData.note || null,
        sent_to_cc: null,
        auth_user: parseInt(isAuth()?.user_id || isAuth()?.id),
        file_path: compFileList,
        is_customer: sendToCustomer,
      };

      if (isNaN(payload.auth_user)) {
        alert("Authentication Error: User ID not found.");
        return;
      }

      const resultAction = await sendProofDispatch(payload);
      if (send_proof.fulfilled.match(resultAction)) {
        // Refresh data
        let params = {
          company_id: job_detail.company_id,
          process_id: job_detail.process_id,
        };
        await get_company_process_stage(params);
        await get_sidebar_menu(job_detail.process_id);
        await get_job_messages({ job_id: jobId });
        await proof_log_table({ job_id: jobId });
      } else {
        const error = resultAction.payload || "Unknown error";
        alert(`Failed to send art: ${typeof error === 'object' ? JSON.stringify(error) : error}`);
      }
    } catch (error) {
      console.error("handleSendProof error:", error);
      alert(`An unexpected error occurred while sending art: ${error.message}`);
    }
  }

  handleSetTabIndex = async (newVal) => {
    this.props.setTabIndex(newVal)
  };

  render() {
    const { mode, job_form, job_data, send_message_loading, tab_index, job_detail, prev_stage, new_stage, proof_log_data, job_update_flag } = this.props;
    const company_process = this.props.company_process || [];
    const process =
      company_process.find(p => String(p.id) === String(job_detail?.process_id)) ||
      company_process.find(p => String(p.process_id) === String(job_detail?.process_id)); // fallback

    const canShowApprovalLog = !!(process?.standard_proof || process?.annotated_proof);
    return (
      <Box sx={{
        pt: 0,
        width: '100%'
      }}>
        <Stack
          direction="row"
          alignItems="stretch"
          spacing={0}
          sx={{ width: '100%', mt: '-5px' }}
        >
          {mode != "detail" ? (
            <JobForm
              mode={mode}
              job_id={this.props.job_id}
              job_form={job_form}
              job_data={job_data}
              job_detail={job_detail}
              prev_stage={prev_stage}
              new_stage={new_stage}
              proof_log_data={proof_log_data}
              handleStatusChange={this.handleStatusChange}
              handleJobForm={this.handleJobForm}
              handleJobSubmit={this.handleJobSubmit}
              handleJobUpdate={this.handleJobUpdate}
              handleZipDownload={this.handleZipDownload}
              job_update_flag={job_update_flag}
            />
          ) : (
            <JobDetail
              mode={mode}
              job_id={this.props.job_id}
              job_form={job_form}
              job_data={job_data}
              job_detail={job_detail}
              proof_log_data={proof_log_data}
              handleZipDownload={this.handleZipDownload}
              handleSendProof={this.handleSendProof}
            />
          )}
        </Stack>
        {mode !== "create" && (
          <>
            <SendMessage
              is_loading={send_message_loading}
              tab_index={tab_index}
              job_id={this.props.job_id}
              action_type={this.action_type}
              proof_log_data={proof_log_data}
              create_send_message={this.handleJobLogSubmit}
              handleSetTabIndex={this.handleSetTabIndex}
              canShowApprovalLog={canShowApprovalLog}
            />
          </>
        )}
      </Box>
    )
  }
}

export default JobPageContainer;
